/**
 * №53 · 53.3 — THE MATTER STREAM. Transferred from `docs/53-mockups/matters-1.png`.
 *
 * Timestamped events by person or hire, day-grouped, with artefact chips and time
 * chips, and the open decision sitting **in the stream where it happened** rather
 * than in a panel beside it. Beside it, the facts rail — and the caption's rule
 * is the one thing about this surface that is not decoration: *"every fact in the
 * rail is a link to the tab that owns it."*
 *
 * ## What is NOT here, and why
 *
 * **Crew time chips.** The drawing hangs `time 0.7h · MR` off a hire's line. A
 * `time-entry` references a **matter**, never an event, so there is nothing to
 * join a hire's hours to a particular line — and attributing a hire's time at all
 * is №54's job (until the server attributes it, the figure is one the hire chose).
 * A recorded entry appears as its own event carrying its own chip, which is what
 * it honestly is. This is E-32's lesson applied one rung later rather than
 * re-learned.
 *
 * **The record's declared fields stay** (`RecordView`, below the stream). The
 * drawing shows the stream and the rail and no field grid, and 53.3 says the
 * stream replaces the Overview COMPOSITE — the Activity / Work / What-we-know
 * grid, which it does replace, entirely. Deleting `RecordView` as well would
 * orphan Key Dates, which no other tab renders: it is declared-sections data with
 * nowhere to go. Raised as E-33 rather than decided by deletion.
 */
import type React from "react";
import { Link } from "react-router";
import type { StreamEvent } from "../../../lib/types";
import { agentPersonaFromId, personaFill } from "../../lib/agent-persona";
import {
  actionFor, clockOf, factsFor, groupByDay, timeThisWeek,
  type MatterFactsInput, type SurfaceRow,
} from "./matter-stream-model";

function ActorName({ e }: { e: StreamEvent }) {
  const name = e.actor?.name ?? e.agentName ?? null;
  if (!name) return null;
  const isHire = e.actor?.kind === "agent";
  return (
    <span
      className="ms-actor"
      style={isHire ? { color: personaFill(agentPersonaFromId(e.actor?.id ?? name)) } : undefined}
    >
      {name}
    </span>
  );
}

function Chips({ e }: { e: StreamEvent }) {
  const arte = e.artefacts ?? [];
  if (arte.length === 0 && !e.time) return null;
  return (
    <>
      {arte.map((a, i) =>
        a.href
          ? <Link key={`${a.label}-${i}`} className="ms-chip" to={a.href}>{a.label}</Link>
          : <span key={`${a.label}-${i}`} className="ms-chip">{a.label}</span>,
      )}
      {e.time && (
        <span className="ms-chip ms-chip--time">
          time {e.time.hours}h · {e.time.who}
        </span>
      )}
    </>
  );
}

export interface MatterStreamProps {
  events: StreamEvent[];
  /** `YYYY-MM-DD`, passed in so a frozen render is stable and a gate can pin it. */
  todayKey: string;
  facts: MatterFactsInput;
  /** №53.3.1 S0 (D-CR26 item 6) — the Surfaces block, already ordered
   *  (`matter-stream-model.ts`'s `surfaceRows`): one row per `_meta._related`
   *  entry, count-only unless the server sent a claim. */
  surfaces?: SurfaceRow[];
  loading?: boolean;
  /**
   * The OPEN DECISION, rendered by the page at the point in the stream where it
   * was raised.
   *
   * A render prop rather than a button, and the reason is a ruling rather than a
   * preference. The drawing puts an `Open the card →` button on the card line, and
   * building exactly that would have taken the matter from FOUR card homes to
   * three — breaking **D-CC4** (one card, all surfaces, provenance on every live
   * one), **F10** (free text answerable on all four homes) and **D-CH2** (answering
   * focuses the panel, at every home), each of which is ratified and each of which
   * has a gate. A decision row wins over a mockup.
   *
   * And the fuller reading is the drawing's own caption: *"the open decision sits
   * in the stream where it happened."* The real card sits there; a button that
   * sends you elsewhere does not.
   */
  renderCard?: (e: StreamEvent) => React.ReactNode;
  onOpenCard?: (e: StreamEvent) => void;
}

export function MatterStream({ events, todayKey, facts, surfaces, loading, renderCard, onOpenCard }: MatterStreamProps) {
  const days = groupByDay(events, todayKey);
  const rail = factsFor(facts);
  const week = timeThisWeek(events);
  const surfaceRows = surfaces ?? [];

  return (
    <div className="ms" data-testid="matter-stream">
      <div className="ms__main">
        {loading && <p className="ms__empty">Reading the matter…</p>}
        {!loading && days.length === 0 && (
          // R7③ — nothing to say is said, not drawn as an empty frame.
          <p className="ms__empty" data-testid="matter-stream-empty">
            Nothing has happened on this matter yet. Every run, decision, filed document and
            recorded hour lands here, in the order it happened.
          </p>
        )}
        {days.map((day) => (
          <section key={day.key} className="ms__day" data-testid="matter-stream-day">
            <h3 className="ms__daylabel">{day.label}</h3>
            {day.events.map((e) => {
              const act = actionFor(e);
              return (
                <div className="ms__ev" key={e.id} data-testid="matter-stream-event" data-kind={e.kind}>
                  <span className="ms__t">{clockOf(e.ts)}</span>
                  <div className="ms__body">
                    {/* The card renders IN PLACE — see `renderCard`. When the page
                        supplies one, the stream steps aside entirely rather than
                        drawing its own summary line above it. */}
                    {e.kind === "card" && renderCard ? renderCard(e) : (
                    <div className="ms__line">
                      <ActorName e={e} />{" "}
                      {e.kind === "card" ? (
                        <>raised a decision — {e.reason}</>
                      ) : e.termination === "blocked_honest" ? (
                        <><b className="ms__blocked">Blocked</b> — {e.summary ?? e.title}</>
                      ) : (
                        <>{e.summary ?? e.title}</>
                      )}{" "}
                      <Chips e={e} />
                    </div>
                    )}
                    {act && (
                      <button
                        type="button"
                        className="ms__act"
                        onClick={() => onOpenCard?.(e)}
                        data-testid="matter-stream-action"
                      >
                        {act.label}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        ))}
      </div>

      <aside className="ms__rail" data-testid="matter-facts">
        <h4 className="ms__railh">Matter facts</h4>
        {rail.length === 0 ? (
          // Every fact absent is itself the honest answer on a matter this thin.
          <p className="ms__railempty">Nothing is known about this matter yet beyond its name.</p>
        ) : (
          <dl className="ms__facts">
            {rail.map((f) => (
              <div key={f.key} className="ms__fact">
                <dt>{f.label}</dt>
                <dd>
                  {f.href
                    ? <Link to={f.href}>{f.value} →</Link>
                    : f.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {surfaceRows.length > 0 && (
          <>
            <h4 className="ms__railh">Surfaces</h4>
            <div className="surfaces" data-testid="matter-surfaces">
              {surfaceRows.map((s) => {
                const cls = `surfrow${s.needsPerson ? " surfrow--needs" : ""}`;
                const body = (
                  <>
                    <div className="surfrow__h">
                      <span className="surfrow__label">{s.label}</span>
                      <span className="surfrow__count">{s.count}</span>
                    </div>
                    {/* Server-composed, rendered verbatim — never edited, never
                        invented (D-CR2). Absent → this row is honestly
                        count-only, which is correct for every surface but
                        Trust/Process/Billing this rung. */}
                    {s.claimText && <p className="surfrow__claim">{s.claimText}</p>}
                  </>
                );
                return s.href
                  ? <Link key={s.key} to={s.href} className={cls} data-testid="matter-surface-row">{body}</Link>
                  : <div key={s.key} className={cls} data-testid="matter-surface-row">{body}</div>;
              })}
            </div>
          </>
        )}
        {week.length > 0 && (
          <>
            <h4 className="ms__railh">Time recorded</h4>
            <dl className="ms__facts" data-testid="matter-time">
              {week.map((r) => (
                <div key={r.who} className="ms__fact">
                  <dt>{r.who}</dt>
                  <dd>{r.hours}h</dd>
                </div>
              ))}
            </dl>
          </>
        )}
      </aside>
    </div>
  );
}
